/**
 * Pure (de)serialization between the erin-app integration form and the CLAUS
 * (integration-config-api) request/response shapes. No React, no network — unit-tested in
 * serde.test.js. The authoritative contracts are integration-config-api's
 * `src/schemas/integration.ts` and `integration-admin/src/types/index.ts`.
 *
 * Two hard CLAUS rules encoded here:
 *   - ATS / metabase creates REQUIRE a `schedule`.
 *   - candidate creates REQUIRE `statusMappings`.
 * And one safety rule: on patch, drop empty auth fields ("leave empty = keep existing" + don't
 * re-transmit unchanged secrets, which CLAUS would republish on the EventBridge bus).
 */

const MINUTES_PER_UNIT = { minute: 1, hour: 60, day: 1440, week: 10080 };

export function toMinutes(value, unit) {
	const per = MINUTES_PER_UNIT[unit];
	if (!per || !Number.isFinite(value)) return null;
	return value * per;
}

// ---- field mappings: flat Record<string,string> (source → target) ----

export function fieldMappingRowsToRecord(rows) {
	const out = {};
	for (const { source, target } of rows || []) {
		const s = (source || '').trim();
		const t = (target || '').trim();
		if (s && t) out[s] = t;
	}
	return out;
}

export function recordToFieldMappingRows(record) {
	return Object.entries(record || {}).map(([source, target]) => ({
		source,
		target: typeof target === 'string' ? target : JSON.stringify(target),
	}));
}

// ---- status mappings (candidate): { statusMap: StatusMappingEntry[] } ----

/** Drop incomplete rows, and only emit sourceStageValue / conditions when actually set. */
export function statusEntriesToStatusMap(entries) {
	const statusMap = (entries || [])
		.filter((m) => (m.sourceValue || '').trim() && (m.targetStatus || '').trim())
		.map((m) => {
			const entry = {
				sourceValue: m.sourceValue.trim(),
				targetStatus: m.targetStatus.trim(),
				targetStage: (m.targetStage || '').trim(),
			};
			if (m.sourceStageValue && m.sourceStageValue.trim()) {
				entry.sourceStageValue = m.sourceStageValue.trim();
			}
			const conditions = (m.conditions || [])
				.filter((c) => c.field && c.field.trim())
				.map((c) => ({ field: c.field.trim(), operator: c.operator || 'is_not_null' }));
			if (conditions.length) entry.conditions = conditions;
			return entry;
		});
	return { statusMap };
}

export function statusMapToEntries(statusMappings) {
	const list = statusMappings?.statusMap;
	return Array.isArray(list) ? list.map((m) => ({ ...m })) : [];
}

// ---- schedule ----

/** Shape a schedule for CLAUS: keep frequency+unit; include the lookback pair only if BOTH set. */
export function normalizeSchedule(schedule) {
	if (!schedule) return undefined;
	const out = { frequency: Number(schedule.frequency), unit: schedule.unit };
	if (schedule.lookbackPeriod && schedule.lookbackPeriodUnit) {
		out.lookbackPeriod = Number(schedule.lookbackPeriod);
		out.lookbackPeriodUnit = schedule.lookbackPeriodUnit;
	}
	return out;
}

/** Returns an error string if the schedule is invalid, else null. */
export function validateSchedule(schedule) {
	if (!schedule || !schedule.frequency || !schedule.unit) return 'A sync frequency is required.';
	if (!(Number(schedule.frequency) >= 1)) return 'Sync frequency must be at least 1.';
	const hasPeriod = !!schedule.lookbackPeriod;
	const hasUnit = !!schedule.lookbackPeriodUnit;
	if (hasPeriod !== hasUnit) return 'Both lookback period and unit must be set together.';
	if (hasPeriod) {
		const lb = toMinutes(Number(schedule.lookbackPeriod), schedule.lookbackPeriodUnit);
		const fr = toMinutes(Number(schedule.frequency), schedule.unit);
		if (lb != null && fr != null && lb < fr) {
			return 'Lookback period must be at least as long as the sync frequency.';
		}
	}
	return null;
}

// ---- auth ----

/** Keep only auth fields the operator actually filled in (empty = keep existing on the server). */
export function dropEmptyAuth(authParams) {
	const out = {};
	for (const [k, v] of Object.entries(authParams || {})) {
		if (v !== undefined && v !== null && String(v).length > 0) out[k] = v;
	}
	return out;
}

// ---- payload builders ----

const isScheduled = (source) => source === 'ats' || source === 'metabase';

/** Assemble the exact IntegrationCreate body CLAUS expects for the chosen template. */
export function buildCreatePayload(form, template) {
	const source = template?.source;
	const type = template?.type;
	const payload = {
		name: (form.name || '').trim(),
		templateId: form.templateId,
		inputParams: form.inputParams || {},
		authParams: dropEmptyAuth(form.authParams),
		fieldMappings: fieldMappingRowsToRecord(form.fieldMappingRows),
	};
	if (isScheduled(source)) payload.schedule = normalizeSchedule(form.schedule);
	if (type === 'candidate') payload.statusMappings = statusEntriesToStatusMap(form.statusEntries);
	if (form.sftpCredentialId) payload.sftpCredentialId = form.sftpCredentialId;
	return payload;
}

/** Assemble an IntegrationPatch; omit authParams entirely when nothing was re-entered. */
export function buildPatchPayload(form, { type, source } = {}) {
	const patch = {
		name: (form.name || '').trim(),
		enabled: !!form.enabled,
		inputParams: form.inputParams || {},
		fieldMappings: fieldMappingRowsToRecord(form.fieldMappingRows),
	};
	if (source !== 'sftp' && form.schedule) patch.schedule = normalizeSchedule(form.schedule);
	if (type === 'candidate') patch.statusMappings = statusEntriesToStatusMap(form.statusEntries);
	const auth = dropEmptyAuth(form.authParams);
	if (Object.keys(auth).length) patch.authParams = auth;
	return patch;
}
